import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import {
	BillingFeature,
	ReportSlideFragmentType,
	type NewReportSlideFragmentLayout,
} from 'webapp/enums/enums';
import { getOrderFromPosition } from 'webapp/utils/common';
import { getWidthWithoutPaddings } from 'webapp/utils/html';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import nodeResize, {
	Orientation as ResizeDirection,
	ResizeMode,
} from 'webapp/modifiers/node-resize';
import reportEditorGridItemInteractions from 'webapp/modifiers/report-editor-grid-item-interactions';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ReportsDisabledOverlayComponent from 'webapp/components/reports/disabled-overlay';
import { getPositionPropertyFromOrderProperty } from 'webapp/components/reports/editor-base';
import type { OrderProperty } from 'webapp/components/reports/editor-base';
import type { SettingsSizeData } from 'webapp/components/reports/fragment-grid';
import ReportsFragmentGridAddColumnComponent from 'webapp/components/reports/fragment-grid/add-column';
import ReportsFragmentGridAddFragmentButtonComponent from 'webapp/components/reports/fragment-grid/add-fragment-button';
import ReportsSkeletonFragmentComponent, {
	CLASS_MODIFIERS,
} from 'webapp/components/reports/skeleton-fragment';

import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

interface Args {
	model: ReportSlideFragment;
	orderProperty: OrderProperty;
	isPageless: boolean;
	isEditable?: boolean; // if true, actions will show on hover (will be inserted to keep spacings if !skipActionsRender)
	slideBackgroundColor?: string | null; // needed to set background color for the button wrapper on slides
	addFragmentInPosition: (
		fragmentType: NewReportSlideFragmentLayout,
		rowIndex: number,
		columnIndex: number,
	) => void;
	removeFragment: (fragment: ReportSlideFragment) => Promise<void>;
	updateFragmentSize: (updates: SettingsSizeData) => void;
	addRow: (fragmentType: NewReportSlideFragmentLayout, afterRowIndex: number) => void;
}

export interface ReportsFragmentGridItemComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class ReportsFragmentGridItemComponent extends Component<
	ReportsFragmentGridItemComponentInterface<Args>
> {
	@service('model-manager/report-slide-fragments')
	declare mmReportsSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('navigation') declare navigationService: Service.Navigation;

	nodeResizeDirection = ResizeDirection.HORIZONTAL;
	nodeResizeDirectionVertical = ResizeDirection.VERTICAL;
	resizeModeInverse = ResizeMode.INVERSE;

	node: HTMLElement | null = null;
	resizerNode: HTMLElement | null = null;

	initialWidth: string | undefined;
	// used for board edge to edge positioning
	@tracked boardLeftMargin: string | undefined;
	@tracked boardWidth: string | undefined;
	@tracked isVisibleOnScreen = false;

	private resizeObserver: ResizeObserver | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		// only for initial style to avoid flickering - grid/index will override it very quick
		// is board check for edge to edge on pageless
		this.initialWidth =
			args.model.isTypeBoard && this.args.isPageless
				? undefined
				: `${args.model.width || 100}%`;

		if (window.isSnapshooterActive || !this.args.model.isOnPagelessReport) {
			this.isVisibleOnScreen = true;
		}
	}

	get positionProperty() {
		return getPositionPropertyFromOrderProperty(this.args.orderProperty);
	}

	get gridItemStyle() {
		const styles: string[] = [];
		if (this.initialWidth) {
			styles.push(`width: ${this.initialWidth};`);
		}
		return htmlSafe(styles.join(' '));
	}

	get fragmentWrapperStyle() {
		const styles: string[] = [];
		if (this.boardLeftMargin) {
			styles.push(`margin-left: ${this.boardLeftMargin};`);
		}
		if (this.boardWidth) {
			styles.push(`width: ${this.boardWidth};`);
		}
		return styles.length ? htmlSafe(styles.join(' ')) : undefined;
	}

	get order() {
		return this.args.model[this.args.orderProperty];
	}

	get row() {
		return this.args.model[this.positionProperty].row;
	}

	get column() {
		return this.args.model[this.positionProperty].column;
	}

	get isInFirstColumn() {
		return this.column === 1;
	}

	get showAddAction() {
		return !this.args.model.isTypeBoard;
	}

	get showRemoveAction() {
		return (
			(this.args.isPageless || !this.args.model.isTypeBoard) && !this.args.model.isTypeBlock
		);
	}

	get showLeftAddColumn() {
		return this.showAddAction && this.column === 1;
	}

	get showResizeHandle() {
		return this.column !== 3;
	}

	get extraWrapperClassString() {
		// title fragment must have bigger top padding (unless slide mode first row or pageless mode first ever)
		// on pageless padding is handled with actions container. row > 2 because of hidden image fragment

		const { model, isPageless } = this.args;
		const shouldAddPadding =
			model.isTypeTitle &&
			// 23 is last column in second row
			(isPageless
				? model[this.args.orderProperty] > getOrderFromPosition(2, 3)
				: model.position.row !== 1);

		return shouldAddPadding ? 'extra-top-padding' : '';
	}

	get trackingOrigin() {
		return this.args.model.disabledLimitation === BillingFeature.GENERATIVE_AI
			? 'generative_ai_block'
			: undefined;
	}

	get skeletonType(): ReportSlideFragmentType {
		return this.args.model.type;
	}

	get skeletonTypeClass(): string {
		const type: ReportSlideFragmentType = this.args.model.type;
		return CLASS_MODIFIERS[type] || '';
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;

		// we need to setup board always because of skeleton edge to edge positioning
		if (!this.isVisibleOnScreen && !this.args.model.isTypeBoard) {
			return;
		}

		// higher delay is needed to allow all items on "slide" to be rendered
		this.handleFragmentInitialization(500);
	}

	@action
	teardownView(): void {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	@action
	onRemoveFragment() {
		const currentRow = this.row;
		const usercontentNode = this.node?.closest<HTMLElement>('.usercontent, .pageless-wrapper');

		const fragmentsInRow =
				usercontentNode?.querySelectorAll<HTMLElement>(
					`.grid-item[data-row="${currentRow}"]`,
				) || [],
			stretchableFragmentsInRow = Array.from(fragmentsInRow).filter((node) =>
				node.classList.contains('stretchable'),
			);

		const isOnlyStretchableInRow =
			stretchableFragmentsInRow.length === 1 &&
			stretchableFragmentsInRow[0]!.getAttribute('data-id') === this.args.model.id;

		this.args
			.removeFragment(this.args.model)
			.then(() => {
				// if this was the only stretchable fragment in the row, remove height from remaining fragments in the row
				if (isOnlyStretchableInRow) {
					fragmentsInRow.forEach((node) => {
						node.style.removeProperty('height');
					});
				}
			})
			.catch(() => {
				// do nothing - user cancelled the deletion
			});
	}

	// ------ START GRID ITEM RESIZER LOGIC -------

	_rightFragment: ReportSlideFragment | null = null;

	@action
	changeWidths() {
		// leftFragment is the one on the left of the resizer
		// rightFragment is the one on right of the resizer
		// otherFragment is the (possible) third fragment in the row (at the start or the end)
		const leftFragment = this.args.model;

		const usercontentNode = this.node?.closest<HTMLElement>('.usercontent, .pageless-content');
		if (!usercontentNode) {
			return;
		}

		const currentRow = this.row;
		const fragmentNodesInSameRow = usercontentNode.querySelectorAll(
			`.grid-item[data-row="${currentRow}"]`,
		);
		const isThreeInRow = fragmentNodesInSameRow.length === 3; // as opposed to two in row

		const leftFragmentNode = usercontentNode.querySelector<HTMLElement>(
				`.grid-item[data-row="${this.row}"][data-column="${this.column}"]`,
			)!,
			rightFragmentNode = leftFragmentNode.nextElementSibling as HTMLElement,
			rightFragmentNodeId = rightFragmentNode.getAttribute('data-id');

		if (rightFragmentNodeId && this._rightFragment?.id !== rightFragmentNodeId) {
			this._rightFragment =
				this.mmReportsSlideFragmentsService.findModel(rightFragmentNodeId)!;
		}

		let otherFragmentNode: HTMLElement | null = null;
		if (isThreeInRow) {
			const leftFragmentNodePreviousSiblingNode = leftFragmentNode.previousElementSibling;
			const rightFragmentNodeNextSiblingNode = rightFragmentNode.nextElementSibling;

			if (
				leftFragmentNodePreviousSiblingNode?.getAttribute('data-row') === String(currentRow)
			) {
				otherFragmentNode = leftFragmentNodePreviousSiblingNode as HTMLElement;
			} else if (
				rightFragmentNodeNextSiblingNode?.getAttribute('data-row') === String(currentRow)
			) {
				otherFragmentNode = rightFragmentNodeNextSiblingNode as HTMLElement;
			}
		}

		const minWidthPercentage = isThreeInRow ? 25 : 30,
			maxWidthPercentage = isThreeInRow ? 50 : 70,
			wholeWidth = Math.floor(getWidthWithoutPaddings(usercontentNode)),
			percentageOfLeftSide = (leftFragmentNode.offsetWidth / wholeWidth) * 100, // because node-resize modifier sets width/height in pixels.
			leftAndRightFragmentWidthPercentSum = isThreeInRow
				? 100 - Number.parseFloat(otherFragmentNode!.style.width)
				: 100;

		const leftFragmentMaxWidthPercent = isThreeInRow
			? Math.min(maxWidthPercentage, leftAndRightFragmentWidthPercentSum - minWidthPercentage)
			: maxWidthPercentage;
		const leftFragmentWidthPercent = Math.min(
				leftFragmentMaxWidthPercent,
				Math.max(minWidthPercentage, percentageOfLeftSide),
			),
			rightFragmentWidthPercent =
				leftAndRightFragmentWidthPercentSum - leftFragmentWidthPercent;

		leftFragmentNode.style.width = `${leftFragmentWidthPercent}%`;
		rightFragmentNode.style.width = `${rightFragmentWidthPercent}%`;

		// Create updates array for both fragments
		const updates: SettingsSizeData = [
			{
				id: leftFragment.id!,
				settings: {
					width: leftFragmentWidthPercent,
				},
			},
		];

		if (this._rightFragment) {
			updates.push({
				id: this._rightFragment.id!,
				settings: {
					width: rightFragmentWidthPercent,
				},
			});
		}

		// Trigger the update with debounce - this will update model properties and save
		debounce(this, this.updateFragmentSize, usercontentNode, updates, 150);
	}

	@action
	changeHeights() {
		const usercontentNode = this.node?.closest<HTMLElement>('.usercontent, .pageless-content');
		if (!usercontentNode) {
			return;
		}

		const baseHeight = this.args.isPageless
			? (usercontentNode.offsetWidth * 9) / 16 // calculate 16:9 ratio
			: usercontentNode.offsetHeight;

		if (this.args.isPageless) {
			this.handlePagelessHeightChange(usercontentNode, baseHeight);
		} else {
			this.handleSlideHeightChange(usercontentNode, baseHeight);
		}
	}

	// Resizing sides
	@action
	updateFragmentSize(usercontentNode: HTMLElement, updates: SettingsSizeData): void {
		// update model properties before saving
		updates.forEach((update) => {
			const fragment = this.mmReportsSlideFragmentsService.findModel(update.id);

			if (fragment) {
				if (update.settings.width !== undefined) {
					fragment.width = update.settings.width;
					fragment.directPropUpdate({ width: update.settings.width });
				}
				if (update.settings.height !== undefined) {
					fragment.height = update.settings.height;
					fragment.directPropUpdate({ height: update.settings.height });
				}
			}
		});

		const previewBlocks = usercontentNode?.querySelectorAll('.preview-block');
		(previewBlocks || []).forEach((previewBlock) => {
			$(previewBlock).trigger('resize.block');
		});

		this.args.updateFragmentSize(updates);
	}

	private handleFragmentInitialization(heightCalculationDelay: number): void {
		// setup resize observer for board fragment positioning
		if (this.args.isPageless && !this.resizeObserver) {
			this.setupResizeObserver();
			this.updateBoardFragmentLeftMarginAndWidth();
		}

		// calculate height for stretchable fragments
		if (this.args.model.isStretchableOnSlide && this.node) {
			later(
				this,
				() => {
					const height = this.calculateInitialHeight();
					if (height && this.node) {
						this.node.style.height = height;
					}
				},
				heightCalculationDelay,
			);
		}
	}

	private handlePagelessHeightChange(usercontentNode: HTMLElement, baseHeight: number) {
		const minHeight = baseHeight * 0.5, // 50% of container height
			maxHeight = baseHeight * 3; // 300% of container height

		// get items in same row
		const currentRow = this.row,
			fragmentNodesInSameRow = usercontentNode.querySelectorAll(
				`.grid-item[data-row="${currentRow}"]`,
			);

		this.updateRowFragmentsHeight(fragmentNodesInSameRow, baseHeight, minHeight, maxHeight);
	}

	private handleSlideHeightChange(usercontentNode: HTMLElement, contentHeight: number) {
		const items = Array.from(usercontentNode.querySelectorAll<HTMLElement>('.grid-item'));
		const itemsByRows: Record<string, HTMLElement[]> = {};

		// group items by row
		items.forEach((item) => {
			const row = item.getAttribute('data-row');
			if (!row) return;

			if (!itemsByRows[row]) {
				itemsByRows[row] = [];
			}
			itemsByRows[row].push(item);
		});

		// separate stretchable and normal rows
		const itemsByStretchableRows: HTMLElement[][] = [];
		const itemsByNormalRows: HTMLElement[][] = [];

		Object.values(itemsByRows).forEach((rowItems) => {
			if (rowItems.some((i) => i.classList.contains('stretchable'))) {
				itemsByStretchableRows.push(rowItems);
			} else {
				itemsByNormalRows.push(rowItems);
			}
		});

		// if this is the only stretchable row, use full content height
		let stretchableHeight: number;
		if (itemsByStretchableRows.length === 1 && itemsByNormalRows.length === 0) {
			stretchableHeight = contentHeight;
		} else {
			let freeSpaceForStretchableRows = contentHeight;
			itemsByNormalRows.forEach((rowItems) => {
				const rowMaxHeight = Math.max(...rowItems.map((item) => item.offsetHeight));
				freeSpaceForStretchableRows -= rowMaxHeight;
			});

			stretchableHeight =
				freeSpaceForStretchableRows / Math.max(itemsByStretchableRows.length, 1);
		}

		const minStretchableHeight = contentHeight / 3;
		const minHeight = Math.max(minStretchableHeight, stretchableHeight * 0.5);

		const currentRow = this.row;
		const currentRowItems = usercontentNode.querySelectorAll(
			`.grid-item[data-row="${currentRow}"]`,
		);

		this.updateRowFragmentsHeight(
			currentRowItems,
			stretchableHeight,
			minHeight,
			stretchableHeight * 3,
		);
	}

	private updateRowFragmentsHeight(
		fragmentNodes: NodeListOf<Element>,
		baseHeight: number,
		minHeight: number,
		maxHeight: number,
	) {
		const usercontentNode = this.node?.closest<HTMLElement>('.usercontent, .pageless-content');
		if (!usercontentNode) {
			return;
		}

		// get current height percentage and constrain it
		const currentHeight = Math.min(maxHeight, Math.max(minHeight, this.node!.offsetHeight)),
			heightPercentage = (currentHeight / baseHeight) * 100;

		// update height for row fragments
		const fragmentHeightUpdates: SettingsSizeData = [];
		fragmentNodes.forEach((node: Element) => {
			(node as HTMLElement).style.height = `${currentHeight}px`;

			// only add to updates if fragment is stretchable (model updates happen in debounced call)
			const fragmentId = node.getAttribute('data-id');
			if (fragmentId) {
				const rowFragment = this.mmReportsSlideFragmentsService.findModel(fragmentId);
				if (rowFragment?.isStretchableOnSlide) {
					fragmentHeightUpdates.push({
						id: fragmentId,
						settings: {
							height: heightPercentage,
						},
					});
				}
			}
		});

		debounce(this, this.updateFragmentSize, usercontentNode, fragmentHeightUpdates, 150);
	}

	// same logic as in updateStretchableFragmentHeight
	private calculateInitialHeight(): string | undefined {
		const container: HTMLElement | null = this.args.isPageless
			? document.querySelector('.pageless-content')
			: document.querySelector('.usercontent');

		if (!container) {
			return;
		}

		const contentHeight = container.offsetHeight;
		let stretchableHeight: number;

		if (this.args.isPageless) {
			stretchableHeight = (container.offsetWidth * 9) / 16; // 16:9 ratio for pageless
		} else {
			const items = Array.from(container.querySelectorAll<HTMLElement>('.grid-item')),
				itemsByRows: Record<string, HTMLElement[]> = {};

			items.forEach((item) => {
				const row = item.getAttribute('data-row');
				if (!row) return;

				if (!itemsByRows[row]) {
					itemsByRows[row] = [];
				}
				itemsByRows[row].push(item);
			});

			// separate stretchable and normal rows
			const itemsByStretchableRows: HTMLElement[][] = [],
				itemsByNormalRows: HTMLElement[][] = [];

			Object.values(itemsByRows).forEach((rowItems) => {
				if (rowItems.some((i) => i.classList.contains('stretchable'))) {
					itemsByStretchableRows.push(rowItems);
				} else {
					itemsByNormalRows.push(rowItems);
				}
			});

			// calculate free space by subtracting normal rows heights
			let freeSpaceForStretchableRows = contentHeight;

			// use the same method as updateStretchableFragmentHeight
			itemsByNormalRows.forEach((rowItems) => {
				let maxHeight = 0;
				rowItems.forEach((item) => {
					const height = item.offsetHeight;
					if (height > maxHeight) {
						maxHeight = height;
					}
				});
				freeSpaceForStretchableRows -= maxHeight;
			});

			// calculate proportional height for stretchable rows
			stretchableHeight =
				freeSpaceForStretchableRows / Math.max(itemsByStretchableRows.length, 1);
		}

		const minStretchableHeight = this.args.isPageless
			? stretchableHeight / 3
			: contentHeight / 3;

		const heightPercentage = this.args.model.height || 100;
		const heightWithMultiplier = stretchableHeight * (heightPercentage / 100);
		return `${Math.max(minStretchableHeight, heightWithMultiplier)}px`;
	}

	private updateBoardFragmentLeftMarginAndWidth() {
		if (!this.node) return;

		const pagelessWrapperElement = this.node.closest<HTMLElement>('.pageless-wrapper')!;
		const pagelessContentElement = this.node.closest<HTMLElement>('.pageless-content')!;

		if (this.args.model.isTypeBoard && pagelessWrapperElement && pagelessContentElement) {
			const pagelessWidth = pagelessWrapperElement.offsetWidth;
			const gridWidth = pagelessContentElement.offsetWidth;
			const marginLeft = (pagelessWidth - gridWidth) / 2;

			// pageless content max width is 800px + 32px horizontal padding
			if (pagelessWidth > 864) {
				this.boardLeftMargin = `-${marginLeft}px`;
			} else {
				this.boardLeftMargin = undefined;
			}
			this.boardWidth = `${pagelessWidth}px`;
		} else {
			this.boardLeftMargin = undefined;
			this.boardWidth = undefined;
		}
	}

	private setupResizeObserver() {
		if (!this.node) {
			return;
		}

		const elementToObserve = this.node.closest('.pageless-wrapper')!;
		if (!elementToObserve) {
			return;
		}

		let previousWidth = elementToObserve.clientWidth;
		this.resizeObserver = new ResizeObserver((entries) => {
			const newWidth = entries[0]!.contentRect.width;
			if (newWidth !== previousWidth) {
				previousWidth = newWidth;
				debounce(this, this.updateBoardFragmentLeftMarginAndWidth, 200);
			}
		});

		this.resizeObserver.observe(elementToObserve);
	}

	// ------ END GRID ITEM RESIZER LOGIC -------

	@action
	addFragmentInMiddle(fragmentType: NewReportSlideFragmentLayout) {
		const currentRow = this.row;
		this.args.addRow(fragmentType, currentRow + 1);
	}

	// ------ DUMMY ITEM LOGIC -------
	get dummyWidth() {
		return this.initialWidth || '100%';
	}

	get dummyHeight() {
		if (!this.node) {
			return '400px';
		}

		const height = (getWidthWithoutPaddings(this.node) * 9) / 16; // calculate 16:9 ratio

		return `${height}px`;
	}

	get dummyStyle() {
		return htmlSafe(`width: ${this.dummyWidth}; height: ${this.dummyHeight};`);
	}

	@action
	visibleChanged(isVisible: boolean): void {
		if (this.isVisibleOnScreen) return;

		this.isVisibleOnScreen = isVisible;

		if (isVisible) {
			const f = this.args.model;
			const shouldSkipBlockFetch =
				this.navigationService.isDatawallApp || f.slide?.report?.isTemplate;

			if (f.blockId && !f.block && !shouldSkipBlockFetch) {
				void this.mmBlocksService.requestSingleBlock(String(f.blockId));
			}

			// small delay to ensure DOM has updated with real data-row
			this.handleFragmentInitialization(100);
		}
	}
	<template>
		<div
			class={{joinClasses
				(array
					"grid-item"
					(if (eq @model.position.column 1) "grid-item--first-column-in-row")
					(if @model.isLastColumnInRow "grid-item--last-column-in-row")
					(if @model.isStretchableOnSlide "stretchable")
					(if @isPageless "visible-check__item")
				)
			}}
			style={{this.gridItemStyle}}
			data-id={{@model.id}}
			data-order={{if this.isVisibleOnScreen this.order -1}}
			data-row={{if this.isVisibleOnScreen this.row -1}}
			data-column={{if this.isVisibleOnScreen this.column -1}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{nodeResize
				this.nodeResizeDirection
				this.changeWidths
				1
				".grid"
				"resize-handle--active"
			}}
			{{nodeResize
				this.nodeResizeDirectionVertical
				this.changeHeights
				1.1
				".grid"
				"resize-handle-vertical"
				this.resizeModeInverse
			}}
			{{reportEditorGridItemInteractions
				isEditable=@isEditable
				isPageless=@isPageless
				row=(if this.isVisibleOnScreen this.row -1)
				column=(if this.isVisibleOnScreen this.column -1)
			}}
		>
			<div
				class={{joinClasses
					(array
						"fragment-wrapper"
						this.extraWrapperClassString
						(if @model.isTypeBoard "fragment-wrapper--board")
					)
				}}
				style={{this.fragmentWrapperStyle}}
			>
				{{#if this.isVisibleOnScreen}}
					{{#if this.showLeftAddColumn}}
						<div class="add-fragment-left-wrapper">
							<ReportsFragmentGridAddColumnComponent
								@fragment={{@model}}
								@addColumn={{@addFragmentInPosition}}
								@orderProperty={{@orderProperty}}
								@isVisible={{@isEditable}}
								@insertBefore={{true}}
								@backgroundColor={{@slideBackgroundColor}}
							/>
							{{#unless @model.isThreeColumnRow}}
								<div class="resize-handle resize-handle--disabled"></div>
							{{/unless}}
						</div>
					{{/if}}
					{{yield}}
					{{#if @model.disabledLimitation}}
						<ReportsDisabledOverlayComponent
							@billingFeature={{@model.disabledLimitation}}
							@trackingOrigin={{this.trackingOrigin}}
						/>
					{{/if}}
					{{#if this.showRemoveAction}}
						{{#if @isEditable}}
							<a
								role="button"
								class="delete-action"
								{{on "click" this.onRemoveFragment}}
							>
								<CommonUiIconComponent @name="Trash" @size="sm" />
							</a>
						{{/if}}
					{{/if}}
				{{else}}
					<div
						class="visible-check__item-placeholder visible-check__item-placeholder--{{this.skeletonTypeClass}}"
						{{visibleCheckItem this.visibleChanged true 2000}}
					>
						<ReportsSkeletonFragmentComponent @type={{this.skeletonType}} />
					</div>
				{{/if}}
			</div>
			{{#if this.showAddAction}}
				{{! We leave grid-item-resizer in DOM since node-resize modifier is triggered always on new item and not on current }}
				<div class="grid-item-resizer">
					<ReportsFragmentGridAddColumnComponent
						@fragment={{@model}}
						@addColumn={{@addFragmentInPosition}}
						@orderProperty={{@orderProperty}}
						@isVisible={{@isEditable}}
						@backgroundColor={{@slideBackgroundColor}}
					/>
					{{#if this.showResizeHandle}}
						<div class="resize-handle resize-handle--active"></div>
					{{/if}}
				</div>
			{{/if}}
			{{#if this.isInFirstColumn}}
				<div class="grid-item-bottom-actions">
					<div class="resize-handle-vertical"></div>
					{{#if @isEditable}}
						<ReportsFragmentGridAddFragmentButtonComponent
							@isVisible={{true}}
							@canAddDataboardSlide={{@isPageless}}
							@extraClass="add-fragment-button--vertical"
							@onAddFragment={{this.addFragmentInMiddle}}
							@backgroundColor={{@slideBackgroundColor}}
						/>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
